'use client';
import { config } from '@/configs/configPortal';
import { handleDispatchInteractionEvent } from '@/helpers/adobe';
import { formatPrice } from '@/helpers/number';
import { getUrlOffers } from '@/helpers/url';
import { capitalize, formatToSlug, isVehicleType } from '@/helpers/utils';
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import CtaDispatch from '../cta-dispatch.component';
import './related-offers.scss';

type RelatedOffersProps = {
    offers: any[];
    brand: string;
    model: string;
    year: string;
    vehicleType: number;
    state?: string;
};

const RelatedOffers = ({ offers, brand, model, year, state, vehicleType }: RelatedOffersProps) => {
    const [emblaRef] = useEmblaCarousel({ align: 'start' });

    const urlOffers = getUrlOffers(vehicleType, brand, model, year.toString(), state ?? '');
    const isCar = isVehicleType(vehicleType, 'car');

    const offersFormated = offers
        .filter((offer) => {
            const requiredFields = [
                offer?.UniqueId,
                offer?.PhotoPath,
                offer?.Specification?.Make?.Value,
                offer?.Specification?.Version?.Value || '', // Ignora Version para motos
                offer?.Specification?.Model?.Value,
                offer?.Seller?.State,
                offer?.Prices?.Price,
            ];
            return requiredFields.every((field) => field !== undefined && field !== null);
        })
        .slice(0, 5).map((offer) => {
            return {
                UniqueId: offer?.UniqueId,
                PhotoPath: `${config.photosUrlUsed}gigante/${offer?.PhotoPath?.split('\\').join('/') || ''}`,
                Make: offer?.Specification?.Make?.Value,
                Version: isCar ? offer?.Specification?.Version?.Value : '',
                Model: offer?.Specification?.Model?.Value,
                Odometer: offer?.Specification?.Odometer,
                NumberPorts: offer?.Specification?.NumberPorts,
                CubicCentimeter: offer?.Specification?.CubicCentimeter,
                State: offer?.Seller?.State,
                Price: offer?.Prices?.Price,
            };
        });

    return (
        offersFormated.length > 0 &&
        <div className="related-offers" data-testid="related-offers" id="related-offers">
            <h2 className="related-offers__title" data-testid="related-offers-title">
                Ofertas para {capitalize(brand)} {capitalize(model)} {capitalize(year.toString())}
            </h2>
            <div className="related-offers__carousel" ref={emblaRef} data-testid="related-offers-carousel" id="related-offers-carousel">
                <div className={`related-offers__container ${offersFormated.length < 4 ? 'align-center' : ''}`} data-testid="related-offers-container" id="related-offers-container">
                    {offersFormated.map((offer) => {
                        const urlDa = isCar
                            ? `${process.env.NEXT_PUBLIC_API_BASE_URL}/comprar/${offer.Make}/${offer.Model}/${formatToSlug(offer.Version)}/${offer.NumberPorts}-portas/${year}-${year}/${offer.UniqueId}`
                            : `${process.env.NEXT_PUBLIC_API_BASE_URL}/comprar/${offer.Make}/${offer.Model}/${offer.CubicCentimeter}cc/${year}-${year}/${offer.UniqueId}`;

                        return (
                            <div
                                className="related-offers__slide"
                                key={offer.UniqueId} data-testid={`related-offers-slide-${offer.UniqueId}`}
                                id={`related-offers-slide-${offer.UniqueId}`}
                                onClick={() => handleDispatchInteractionEvent("ofertas-recomendadas-resultado")}
                            >
                                <a href={urlDa} target="_blank" className="related-offers__card__link" data-testid={`related-offers-card-link-${offer.UniqueId}`} id={`related-offers-card-link-${offer.UniqueId}`}>
                                    <div className="related-offers__card" data-testid={`related-offers-card-${offer.UniqueId}`} id={`related-offers-card-${offer.UniqueId}`}>
                                        <Image src={offer.PhotoPath} width={242} height={190} alt={`Carro ${offer.Version}`} loading='lazy' data-testid={`related-offers-image-${offer.UniqueId}`} id={`related-offers-image-${offer.UniqueId}`} />

                                        <p className="related-offers__card__title" data-testid={`related-offers-title-${offer.UniqueId}`} id={`related-offers-title-${offer.UniqueId}`}>{offer.Make}</p>
                                        {isCar && <p className="related-offers__card__subtitle" data-testid={`related-offers-subtitle-${offer.UniqueId}`} id={`related-offers-subtitle-${offer.UniqueId}`}>{offer.Version}</p>}

                                        <div className="related-offers__card__details" data-testid={`related-offers-details-${offer.UniqueId}`} id={`related-offers-details-${offer.UniqueId}`}>
                                            <p>
                                                <Image
                                                    src={`${config.urlTableFipe}/assets/img/icon/icon-calendar-result.svg`}
                                                    width={16}
                                                    height={16}
                                                    loading='lazy'
                                                    alt='icon calendar'
                                                    data-testid={`related-offers-calendar-icon-${offer.UniqueId}`}
                                                    id={`related-offers-calendar-icon-${offer.UniqueId}`}
                                                />{' '}
                                                {year}/{year}
                                            </p>

                                            <p>
                                                <Image
                                                    src={`${config.urlTableFipe}/assets/img/icon/icon-odometer-result.svg`}
                                                    width={16}
                                                    height={16}
                                                    loading='lazy'
                                                    alt='icon odometer'
                                                    data-testid={`related-offers-odometer-icon-${offer.UniqueId}`}
                                                    id={`related-offers-odometer-icon-${offer.UniqueId}`}
                                                />{' '}
                                                {offer.Odometer} km
                                            </p>
                                        </div>

                                        <div className="related-offers__card__details" data-testid={`related-offers-location-details-${offer.UniqueId}`} id={`related-offers-location-details-${offer.UniqueId}`}>
                                            <p>
                                                <Image
                                                    src={`${config.urlTableFipe}/assets/img/icon/icon-location-result.svg`}
                                                    width={16}
                                                    height={16}
                                                    loading='lazy'
                                                    alt='icon location'
                                                    data-testid={`related-offers-location-icon-${offer.UniqueId}`}
                                                    id={`related-offers-location-icon-${offer.UniqueId}`}
                                                />{' '}
                                                {offer.State}
                                            </p>
                                        </div>

                                        <p className="related-offers__card__price" data-testid={`related-offers-price-${offer.UniqueId}`} id={`related-offers-price-${offer.UniqueId}`}>{formatPrice(offer.Price)}</p>
                                    </div>
                                </a>
                            </div>
                        );
                    })}
                </div>

                <CtaDispatch
                    interaction="ofertas-recomendadas-resultado"
                    href={urlOffers}
                    className="related-offers__look-all"
                    dataTestId="related-offers-look-all"
                    id="related-offers-look-all"
                >
                    Ver todas{' '}

                    <Image src={`${process.env.NEXT_PUBLIC_ASSETS_PATH}/assets/img/icon/icon-arrow-carousel-right.svg`} width={10} height={10} alt='icon arrow carousel' loading='lazy' data-testid="related-offers-arrow-icon" id="related-offers-arrow-icon" />
                </CtaDispatch>
            </div>
        </div>
    );
};

export default RelatedOffers;